import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Copy, Film, MousePointerClick, RefreshCw, Send } from 'lucide-react';
import FeaturedGymClicksCard from './FeaturedGymClicksCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const KIND_COLS = [
  ['website', 'Website'], ['buy_class', 'Buy Class'], ['pricing', 'Pricing'],
  ['social', 'Social'], ['phone', 'Phone'], ['email', 'Email'], ['maps', 'Maps'], ['other', 'Other'],
];

const InviteRow = ({ inv, token, onSent }) => {
  const [email, setEmail] = useState(inv.email || '');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');

  const inviteLink = inv.upload_token ? `${window.location.origin}/gym-upload/${inv.upload_token}` : inv.upload_url;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = inviteLink;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendInvite = async () => {
    setSending(true);
    setErr('');
    try {
      const res = await fetch(`${API_URL}/api/admin/gym-invites/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ gym_id: inv.gym_id, email }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Send failed');
      onSent();
    } catch (e) { setErr(e.message); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`invite-row-${inv.gym_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[180px]">
          <div className="flex items-center gap-2">
            <h4 className="font-bold text-gray-900 text-sm">{inv.name}</h4>
            {inv.has_video ? (
              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-emerald-50 text-emerald-600 border border-emerald-200 rounded inline-flex items-center gap-1"><Film className="w-3 h-3" /> Has Video</span>
            ) : (
              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-amber-50 text-amber-600 border border-amber-200 rounded">No Video</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{inv.city}{inv.state ? `, ${inv.state}` : ''} · {inv.directory}</p>
          {inv.last_invite && (
            <p className="text-[11px] text-emerald-600 mt-0.5">Invite sent to {inv.last_invite.email} on {new Date(inv.last_invite.sent_at).toLocaleDateString()}</p>
          )}
        </div>
        <button
          onClick={copyLink}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 inline-flex items-center gap-1.5"
          data-testid={`invite-copy-${inv.gym_id}`}
        >
          {copied ? <><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Copied</> : <><Copy className="w-3 h-3" /> Copy Link</>}
        </button>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="gym@email.com"
          className="w-52 px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:border-blue-400 outline-none"
          data-testid={`invite-email-${inv.gym_id}`}
        />
        <button
          onClick={sendInvite}
          disabled={sending || !email.includes('@')}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 inline-flex items-center gap-1.5"
          data-testid={`invite-send-${inv.gym_id}`}
        >
          {sending ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send Invite
        </button>
      </div>
      {err && <p className="text-xs text-red-500 mt-2">{err}</p>}
    </div>
  );
};

export default function GymReferralsTab() {
  const token = sessionStorage.getItem('admin_token');
  const [days, setDays] = useState(30);
  const [clicks, setClicks] = useState(null);
  const [invites, setInvites] = useState(null);
  const [blasting, setBlasting] = useState(false);
  const [blastResult, setBlastResult] = useState(null);

  const fetchClicks = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/gym-clicks?days=${days}`, { headers: { Authorization: `Bearer ${token}` } });
      setClicks(await res.json());
    } catch (e) { console.error('gym-clicks fetch failed', e); }
  }, [token, days]);

  const fetchInvites = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/gym-invites`, { headers: { Authorization: `Bearer ${token}` } });
      setInvites(await res.json());
    } catch (e) { console.error('gym-invites fetch failed', e); }
  }, [token]);

  useEffect(() => { fetchClicks(); }, [fetchClicks]);
  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const eligibleCount = (invites?.invites || []).filter((i) => {
    if (!i.email || i.has_video) return false;
    if (i.last_invite && (Date.now() - new Date(i.last_invite.sent_at).getTime()) < 7 * 86400000) return false;
    return true;
  }).length;

  const sendBlast = async () => {
    if (!window.confirm(`Send the video upload invite to ${eligibleCount} gym${eligibleCount === 1 ? '' : 's'} with known emails?\n\n(Skips gyms that already have a video or were invited in the last 7 days.)`)) return;
    setBlasting(true);
    setBlastResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/gym-invites/blast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ dry_run: false }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Blast failed');
      setBlastResult(data);
      fetchInvites();
    } catch (e) {
      setBlastResult({ error: e.message });
    } finally {
      setBlasting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="gym-referrals-tab">
      <FeaturedGymClicksCard />
      <FeaturedGymClicksCard gymId="crossfit-boston-iron-grit" />

      {/* Click digest */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Gym Referral Clicks</h2>
            <p className="text-sm text-gray-500">Outbound clicks from directory pages — proof of value for gyms</p>
          </div>
          <div className="flex gap-2">
            {[[7, '7 days'], [30, '30 days'], [0, 'All time']].map(([d, label]) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${days === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid={`clicks-period-${d}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl p-4 border border-gray-100 mb-4 inline-flex items-center gap-3">
          <MousePointerClick className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-2xl font-bold text-blue-700" data-testid="clicks-total">{clicks?.total_clicks ?? '—'}</p>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total outbound clicks {days ? `(last ${days}d)` : '(all time)'}</p>
          </div>
        </div>

        {!clicks ? (
          <div className="text-center py-8"><RefreshCw className="w-5 h-5 text-gray-400 animate-spin mx-auto" /></div>
        ) : clicks.rows.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-xl"><p className="text-gray-400 text-sm">No outbound clicks tracked yet for this period — they'll appear as visitors click gym links.</p></div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
            <table className="w-full text-sm" data-testid="clicks-table">
              <thead>
                <tr className="border-b border-gray-100 text-left text-[11px] uppercase tracking-wider text-gray-400">
                  <th className="px-4 py-3">Gym</th>
                  <th className="px-4 py-3">City</th>
                  {KIND_COLS.map(([k, label]) => <th key={k} className="px-3 py-3 text-center">{label}</th>)}
                  <th className="px-4 py-3 text-center">Total</th>
                </tr>
              </thead>
              <tbody>
                {clicks.rows.map((r) => (
                  <tr key={r.gym_id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`clicks-row-${r.gym_id}`}>
                    <td className="px-4 py-2.5 font-semibold text-gray-900">
                      {r.name} {r.claimed && <span className="ml-1 px-1 py-0.5 text-[9px] font-bold uppercase bg-emerald-50 text-emerald-600 rounded">Claimed</span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{r.city}{r.state ? `, ${r.state}` : ''}</td>
                    {KIND_COLS.map(([k]) => (
                      <td key={k} className="px-3 py-2.5 text-center text-gray-600">{r.kinds[k] || ''}</td>
                    ))}
                    <td className="px-4 py-2.5 text-center font-bold text-blue-700">{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Video invites */}
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Video Invites — One-Tap Upload Links</h2>
            <p className="text-sm text-gray-500">Every claimed gym gets a private upload link. Copy it or email it — their video lands in the Gym Directory approval queue.</p>
          </div>
          <button
            onClick={sendBlast}
            disabled={blasting || eligibleCount === 0}
            className="px-4 py-2 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 inline-flex items-center gap-1.5"
            data-testid="invite-blast-btn"
          >
            {blasting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {blasting ? 'Sending…' : `Send All Invites (${eligibleCount})`}
          </button>
        </div>
        {blastResult && (
          <div className={`mb-4 px-4 py-3 rounded-xl text-sm border ${blastResult.error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`} data-testid="invite-blast-result">
            {blastResult.error
              ? `Blast failed: ${blastResult.error}`
              : `Sent ${blastResult.sent.length} invite${blastResult.sent.length === 1 ? '' : 's'}${blastResult.failed.length ? ` · ${blastResult.failed.length} failed` : ''}${blastResult.skipped.length ? ` · ${blastResult.skipped.length} skipped (no email, has video, or invited recently)` : ''}`}
          </div>
        )}
        {!invites ? (
          <div className="text-center py-8"><RefreshCw className="w-5 h-5 text-gray-400 animate-spin mx-auto" /></div>
        ) : invites.invites.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-xl"><p className="text-gray-400 text-sm">No claimed gyms yet — claimed listings appear here with their upload link.</p></div>
        ) : (
          <div className="space-y-3">
            {invites.invites.map((inv) => (
              <InviteRow key={inv.gym_id} inv={inv} token={token} onSent={fetchInvites} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
